import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { Box, Container, Typography, Paper, Divider, Link } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useDispatch } from 'react-redux';
import { setUser } from '../store/userSlice';
import { FormikTextField } from '../components/FormikTextField';
import { FormikSubmitButton } from '../components/FormikSubmitButton';
import { useLoginMutation } from '../services/authApi';
import { useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useState } from 'react';
import { IconButton, InputAdornment } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Button } from '@mui/material';
import { useMsal } from '@azure/msal-react';

const signInWithMicrosoftEnabled = typeof import.meta.env.VITE_MSAL_CLIENT_ID !== 'undefined';
const validationSchema = yup.object({
  email: yup.string().email('Enter a valid email').required('Email is required'),
  password: yup
    .string()
    .min(4, 'Password should be of minimum 8 characters length')
    .required('Password is required'),
});

const Login = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [login] = useLoginMutation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation() as any;
  const from = (location.state?.from as string) || '/';
  const [showPassword, setShowPassword] = useState(false);
  const { instance } = useMsal();

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Typography component="h1" variant="h5">
            Sign in with your Swiftflow Payments account
          </Typography>
          <Formik
            initialValues={{
              email: '',
              password: '',
            }}
            validationSchema={validationSchema}
            onSubmit={async (values) => {
              try {
                const response = await login(values).unwrap();

                // If backend indicates MFA enrollment is expired, go to MFA setup
                if (response?.mfaEnforcement === 'expired' && response?.preAuthToken) {
                  navigate('/mfa/setup', {
                    state: { from, preAuthToken: response.preAuthToken, requireSetup: true },
                    replace: true,
                  });
                  return;
                }

                // If backend indicates 2FA is required, go to 2FA page with preAuthToken
                if (response?.requires2FA && response?.preAuthToken) {
                  navigate('/login/2fa', {
                    state: {
                      preAuthToken: response.preAuthToken,
                      from,
                      availableMfaMethods: response.availableMfaMethods,
                      enrollableMfaMethods: response.enrollableMfaMethods,
                      preferredMfaMethod: response.preferredMfaMethod,
                    },
                    replace: true,
                  });
                  return;
                }

                // Otherwise, we should have a JWT; set user and continue
                const userData = {
                  userId: response.userId || null,
                  name: response.name || null,
                  email: response.email || values.email,
                  role: response.role || null,
                  jwtToken: response.jwtToken || null,
                  amcIds: response.amcIds,
                  amcAccess: response.amcAccess,
                };
                dispatch(setUser(userData));

                // If MFA enrollment is required, always show the setup prompt (no suppression)
                if (response?.mfaEnrollmentRequired) {
                  navigate('/mfa/setup-prompt', {
                    state: {
                      from,
                      availableMfaMethods: response.availableMfaMethods,
                      enrollableMfaMethods: response.enrollableMfaMethods,
                      preferredMfaMethod: response.preferredMfaMethod,
                      preAuthToken: response.preAuthToken,
                    },
                    replace: true,
                  });
                } else {
                  navigate(from, { replace: true });
                }
              } catch (error: any) {
                const errorMessage = error?.data?.message || 'An error occurred during login';
                enqueueSnackbar(errorMessage, { variant: 'error' });
              }
            }}
          >
            <Form style={{ width: '100%', marginTop: '8px' }}>
              <FormikTextField
                name="email"
                margin="normal"
                label="Email Address"
                autoComplete="email"
                autoFocus
              />
              <FormikTextField
                name="password"
                margin="normal"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Link component={RouterLink} to="/forgot-password" variant="body2" sx={{ textDecoration: 'none' }}>
                  Forgot password?
                </Link>
              </Box>
              <FormikSubmitButton
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                loadingText="Signing in..."
              >
                Sign In
              </FormikSubmitButton>
            </Form>
          </Formik>
          {signInWithMicrosoftEnabled && (
            <Box sx={{ width: '100%', mt: 2, mb: 1 }}>
              <Divider>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  OR
                </Typography>
              </Divider>
            </Box>
          )}
          {signInWithMicrosoftEnabled && (
            <Box sx={{ mt: 2, width: '100%' }}>
              <Button
                fullWidth
                variant="outlined"
                aria-label="Sign in with Microsoft"
                onClick={async () => {
                  // Preserve intended destination and start redirect
                  sessionStorage.setItem('sso_from', from);
                  await instance.loginRedirect({
                    scopes: ['openid', 'profile', 'email'],
                    prompt: 'select_account',
                  });
                }}
                sx={{
                  textTransform: 'none',
                  color: 'rgba(0,0,0,0.87)',
                  backgroundColor: '#fff',
                  border: '2px solid #8c8c8c',
                  borderColor: '#8c8c8c',
                  borderRadius: 0.5, // ~4px
                  height: 40,
                  px: 2,
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                    borderColor: '#8c8c8c',
                  },
                  '&:focus-visible': {
                    outline: '2px solid #0078D4',
                    outlineOffset: 2,
                  },
                }}
              >
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    component="span"
                    aria-hidden
                    sx={{ width: 20, height: 20, display: 'inline-block' }}
                  >
                    {/* Microsoft 4-color logo */}
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden
                    >
                      <rect x="1" y="1" width="10" height="10" fill="#F25022" />
                      <rect x="13" y="1" width="10" height="10" fill="#7FBA00" />
                      <rect x="1" y="13" width="10" height="10" fill="#00A4EF" />
                      <rect x="13" y="13" width="10" height="10" fill="#FFB900" />
                    </svg>
                  </Box>
                  <Typography
                    component="span"
                    sx={{
                      fontWeight: 600,
                      fontFamily: `'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, 'Noto Sans', 'Liberation Sans', sans-serif`,
                      fontSize: 14,
                    }}
                  >
                    Sign in with Microsoft
                  </Typography>
                </Box>
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;
